import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Panel, TactLoader } from "../components/Tactical";
import { Crown, ExternalLink, Search } from "lucide-react";

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString([], { month: "short", day: "2-digit", year: "numeric" }); }
  catch { return iso; }
}

function StatusBadge({ status }) {
  const map = {
    pending: { cls: "text-[#EA580C] border-[#EA580C]", label: "PENDING" },
    locked: { cls: "text-[#00FF87] border-[#00FF87]", label: "IN PROGRESS" },
    completed: { cls: "text-[#71717A] border-[#71717A]", label: "COMPLETED" },
  };
  const s = map[status] || map.pending;
  return (
    <span className={`font-mono text-[10px] uppercase tracking-[0.25em] border px-2 py-0.5 ${s.cls}`}>
      {s.label}
    </span>
  );
}

const STATUS_FILTERS = [
  { id: "all", label: "All" },
  { id: "pending", label: "Pending" },
  { id: "locked", label: "In progress" },
  { id: "completed", label: "Completed" },
];

export default function AdminByolTab() {
  const [data, setData] = useState({ summary: { pending: 0, locked: 0, completed: 0, total: 0 }, leagues: [] });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const nav = useNavigate();

  const refresh = async () => {
    try {
      const { data } = await api.get("/admin/byol");
      setData(data);
    } catch (e) { /* noop */ }
    finally { setLoading(false); }
  };

  useEffect(() => { refresh(); const iv = setInterval(refresh, 30000); return () => clearInterval(iv); }, []);

  const { summary, leagues } = data;

  const visibleLeagues = useMemo(() => {
    let list = leagues;
    if (filter !== "all") list = list.filter((lg) => lg.status === filter);
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter((lg) =>
        lg.name.toLowerCase().includes(q) ||
        (lg.captain_username || "").toLowerCase().includes(q) ||
        lg.id.toLowerCase().includes(q)
      );
    }
    return list;
  }, [leagues, filter, query]);

  const openLeague = (lg) => {
    // Pending → captain/roster view shows invite code; started/completed → full
    // league detail landing page (standings / fixtures / players / feed / chat).
    if (lg.status === "pending") nav(`/byol/${lg.id}`);
    else nav(`/leagues/${lg.id}`);
  };

  if (loading) return <TactLoader label="LOADING BYOL" />;

  return (
    <div className="space-y-6" data-testid="admin-byol-tab">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#7C3AED] mb-1">// private leagues dashboard</div>
        <h2 className="font-heading text-3xl uppercase tracking-wider">BYOL</h2>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="byol-summary">
        <div className="bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] border-l-2 border-l-[#7C3AED] p-5">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">TOTAL ALL-TIME</div>
          <div className="font-heading text-3xl text-[#09090B] dark:text-[#F4F4F5]" data-testid="byol-stat-total">{summary.total}</div>
        </div>
        <div className="bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] border-l-2 border-l-[#EA580C] p-5">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">PENDING</div>
          <div className="font-heading text-3xl text-[#EA580C]" data-testid="byol-stat-pending">{summary.pending || 0}</div>
        </div>
        <div className="bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] border-l-2 border-l-[#00FF87] p-5">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">IN PROGRESS</div>
          <div className="font-heading text-3xl text-[#00FF87]" data-testid="byol-stat-locked">{summary.locked || 0}</div>
        </div>
        <div className="bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] p-5">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">COMPLETED</div>
          <div className="font-heading text-3xl text-[#71717A]" data-testid="byol-stat-completed">{summary.completed || 0}</div>
        </div>
      </div>

      {/* Filter / search toolbar */}
      <div className="flex items-center gap-3 flex-wrap" data-testid="byol-filter-bar">
        <div className="flex gap-1 border border-[#E4E4E7] dark:border-[#2A2A30] p-1">
          {STATUS_FILTERS.map((f) => {
            const active = filter === f.id;
            return (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                data-testid={`byol-filter-${f.id}`}
                className={`px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] transition-colors ${
                  active
                    ? "bg-[#7C3AED] text-white"
                    : "text-[#71717A] hover:text-[#09090B] dark:hover:text-[#F4F4F5]"
                }`}
              >
                {f.label}
              </button>
            );
          })}
        </div>
        <div className="flex-1 min-w-[200px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#71717A]" aria-hidden />
          <input
            data-testid="byol-search-input"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, captain or ID…"
            className="w-full bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] pl-9 pr-3 py-2 font-mono text-xs text-[#09090B] dark:text-[#F4F4F5] focus:outline-none focus:border-[#7C3AED]"
          />
        </div>
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]" data-testid="byol-visible-count">
          {visibleLeagues.length} / {leagues.length}
        </span>
      </div>

      {/* League list */}
      {leagues.length === 0 ? (
        <Panel className="font-mono text-[#71717A] text-sm">[ NO BYOL LEAGUES CREATED YET. ]</Panel>
      ) : visibleLeagues.length === 0 ? (
        <Panel className="font-mono text-[#71717A] text-sm" testId="admin-byol-empty-filtered">
          [ NO BYOL LEAGUES MATCH THE CURRENT FILTER. ]
        </Panel>
      ) : (
        <div
          className="bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] overflow-x-auto"
          data-testid="admin-byol-list"
        >
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E4E4E7] dark:border-[#2A2A30]">
                <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">LEAGUE</th>
                <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">CAPTAIN</th>
                <th className="text-right py-3 px-4 font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">PLAYERS</th>
                <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">STATUS</th>
                <th className="text-right py-3 px-4 font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">CREATED</th>
                <th className="text-right py-3 px-4 font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">STARTED</th>
                <th className="text-right py-3 px-4 font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">VIEW</th>
              </tr>
            </thead>
            <tbody>
              {visibleLeagues.map((lg) => (
                <tr
                  key={lg.id}
                  onClick={() => openLeague(lg)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openLeague(lg); } }}
                  role="link"
                  tabIndex={0}
                  className="border-b border-[#E4E4E7] dark:border-[#2A2A30] cursor-pointer hover:bg-[#7C3AED]/5 focus:bg-[#7C3AED]/10 focus:outline-none transition-colors"
                  data-testid={`admin-byol-row-${lg.id}`}
                >
                  <td className="py-3 px-4">
                    <div className="font-heading uppercase text-[#09090B] dark:text-[#F4F4F5]">{lg.name}</div>
                    <div className="font-mono text-[9px] text-[#71717A] tracking-[0.2em]">ID {lg.id.slice(0, 8)}</div>
                  </td>
                  <td className="py-3 px-4">
                    <Link
                      to={`/players/${encodeURIComponent(lg.captain_username)}`}
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1 font-heading uppercase text-[#09090B] dark:text-[#F4F4F5] hover:text-[#7C3AED]"
                      data-testid={`admin-byol-captain-${lg.id}`}
                    >
                      <Crown className="w-3 h-3 text-[#F59E0B]" /> {lg.captain_username}
                    </Link>
                  </td>
                  <td className="py-3 px-4 text-right font-mono text-sm">{lg.player_count}/{lg.size_limit}</td>
                  <td className="py-3 px-4"><StatusBadge status={lg.status} /></td>
                  <td className="py-3 px-4 text-right font-mono text-[10px] text-[#71717A]">{fmt(lg.created_at)}</td>
                  <td className="py-3 px-4 text-right font-mono text-[10px] text-[#71717A]">{fmt(lg.started_at)}</td>
                  <td className="py-3 px-4 text-right">
                    <Link
                      to={lg.status === "pending" ? `/byol/${lg.id}` : `/leagues/${lg.id}`}
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`admin-byol-view-${lg.id}`}
                      className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.25em] text-[#7C3AED] hover:underline"
                    >
                      <ExternalLink className="w-3 h-3" /> Open
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
